import React from "react";
import {
  AbsoluteFill,
  interpolate,
  OffthreadVideo,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { Compass, Droplets, Layers } from "lucide-react";

export const SejarahSumurBor: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Entrance spring animation for header
  const titleSpring = spring({
    frame,
    fps,
    config: { damping: 16, mass: 0.8 },
  });

  // 4 Narrative Phases across 300 frames (10 seconds @ 30fps)
  // Phase 1: Frame 0-75    (Masalah Sumur Gali Tradisional)
  // Phase 2: Frame 75-150  (Dinasti Han 200 SM - Pipa Bambu 1000m)
  // Phase 3: Frame 150-225 (Lembah Artois 1126 M - Tekanan Artesis Alami)
  // Phase 4: Frame 225-300 (Revolusi Rotary Bit - Menembus Akuifer Murni)
  const phase =
    frame < 75 ? 1 : frame < 150 ? 2 : frame < 225 ? 3 : 4;

  // Subtitle fade transition per phase
  const phaseFrame =
    frame < 75
      ? frame
      : frame < 150
      ? frame - 75
      : frame < 225
      ? frame - 150
      : frame - 225;

  const textSpring = spring({
    frame: phaseFrame,
    fps,
    config: { damping: 15, mass: 0.7 },
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#0B0D12",
        color: "#FFFFFF",
        fontFamily: "'Poppins', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* 1. FULL-SCREEN 3D ANIMATION (100% CANVAS FILL - NO BOXES) */}
      <AbsoluteFill>
        <OffthreadVideo
          src={staticFile("sumur_bor/clip_3d_intra.mp4")}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transform: "scale(1.04)",
          }}
        />
      </AbsoluteFill>

      {/* 2. SUBTLE CINEMATIC GRADIENT MASKS (Text Legibility Protection) */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 380,
          background:
            "linear-gradient(180deg, rgba(11,13,18,0.88) 0%, rgba(11,13,18,0.45) 60%, transparent 100%)",
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: 540,
          background:
            "linear-gradient(0deg, rgba(11,13,18,0.94) 0%, rgba(11,13,18,0.55) 65%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      {/* 3. TOP EDITORIAL HEADER (Safe Zone Y: 220px - 380px) */}
      <div
        style={{
          position: "absolute",
          top: 220,
          left: "50%",
          transform: `translateX(-50%) translateY(${interpolate(
            titleSpring,
            [0, 1],
            [-20, 0]
          )}px)`,
          width: 920,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          opacity: titleSpring,
          zIndex: 20,
        }}
      >
        {/* Sleek Category Badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            backgroundColor: "rgba(14, 165, 233, 0.25)",
            border: "1px solid rgba(56, 189, 248, 0.5)",
            padding: "6px 18px",
            borderRadius: 100,
            marginBottom: 12,
            backdropFilter: "blur(10px)",
          }}
        >
          <Compass size={14} color="#38BDF8" />
          <span
            style={{
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: 2.2,
              color: "#38BDF8",
              textTransform: "uppercase",
            }}
          >
            SEJARAH & REKAYASA TEKNOLOGI
          </span>
        </div>

        {/* Full Display Title */}
        <h1
          style={{
            margin: 0,
            fontSize: 48,
            fontWeight: 900,
            textAlign: "center",
            textTransform: "uppercase",
            letterSpacing: -0.5,
            lineHeight: 1.15,
            textShadow: "0 4px 18px rgba(0,0,0,0.95)",
          }}
        >
          SEJARAH TERCIPTANYA{" "}
          <span style={{ color: "#38BDF8" }}>SUMUR BOR</span>
        </h1>
      </div>

      {/* 4. FLOATING HUD ANNOTATION (Depth & Geology Strata Indicator) */}
      <div
        style={{
          position: "absolute",
          right: 80,
          top: "45%",
          transform: "translateY(-50%)",
          display: "flex",
          flexDirection: "column",
          gap: 12,
          zIndex: 15,
        }}
      >
        <div
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.8)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
            padding: "8px 16px",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.5)",
          }}
        >
          <Layers size={16} color="#FBBF24" />
          <span style={{ fontSize: 13, fontWeight: 700, color: "#E2E8F0" }}>
            PENAMPANG GEOLOGI 3D
          </span>
        </div>

        <div
          style={{
            backgroundColor: "rgba(14, 165, 233, 0.85)",
            border: "1px solid rgba(56, 189, 248, 0.6)",
            padding: "8px 16px",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
            boxShadow: "0 6px 20px rgba(14, 165, 233, 0.4)",
          }}
        >
          <Droplets size={16} color="#FFFFFF" />
          <span style={{ fontSize: 13, fontWeight: 800, color: "#FFFFFF" }}>
            AKUIFER DALAM BEBAS POLUSI
          </span>
        </div>
      </div>

      {/* 5. DYNAMIC NARRATIVE SUBTITLE (Safe Zone Y: 1220px - 1520px) */}
      <div
        style={{
          position: "absolute",
          bottom: 390,
          left: "50%",
          transform: `translateX(-50%) translateY(${interpolate(
            textSpring,
            [0, 1],
            [20, 0]
          )}px)`,
          width: 920,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          opacity: textSpring,
          zIndex: 20,
        }}
      >
        {/* Historical Phase Badge */}
        <div
          style={{
            backgroundColor: "#0284C7",
            padding: "7px 20px",
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: 1.5,
            textTransform: "uppercase",
            marginBottom: 14,
            boxShadow: "0 4px 16px rgba(2, 132, 199, 0.5)",
          }}
        >
          {phase === 1 && "Fase 1: Keterbatasan Sumur Gali Kuno"}
          {phase === 2 && "Fase 2: Dinasti Han (200 SM - China)"}
          {phase === 3 && "Fase 3: Sumur Artesis (1126 M - Prancis)"}
          {phase === 4 && "Fase 4: Era Modern Rotary Bit"}
        </div>

        {/* Narrative Box (Semi-transparent backdrop pill) */}
        <div
          style={{
            width: "100%",
            backgroundColor: "rgba(15, 23, 42, 0.85)",
            backdropFilter: "blur(14px)",
            border: "1.5px solid rgba(56, 189, 248, 0.35)",
            borderRadius: 22,
            padding: "24px 30px",
            boxShadow: "0 18px 45px rgba(0, 0, 0, 0.65)",
          }}
        >
          <p
            style={{
              margin: 0,
              fontSize: 23,
              lineHeight: 1.45,
              fontWeight: 600,
              textAlign: "center",
              color: "#F8FAFC",
              textShadow: "0 2px 8px rgba(0,0,0,0.8)",
            }}
          >
            {phase === 1 && (
              <>
                Dulu manusia hanya mengandalkan <strong>sumur gali dangkal</strong>. Masalahnya:
                airnya keruh, rawan kering saat kemarau, dan mudah tercemar bakteri dari kotoran
                permukaan tanah.
              </>
            )}
            {phase === 2 && (
              <>
                Tahun <strong>200 SM di China</strong>, lahir teknik bor tumbuk pertama dengan{" "}
                <strong>pipa bambu</strong>. Mereka sukses menembus bumi hingga kedalaman{" "}
                <span style={{ color: "#38BDF8" }}>1.000 meter</span> untuk mengambil air asin dan gas
                alam!
              </>
            )}
            {phase === 3 && (
              <>
                Tahun <strong>1126 di Lembah Artois, Prancis</strong>, ditemukan sumur yang{" "}
                <strong>airnya memancar sendiri</strong> ke atas tanpa ditimba—dikenal sebagai{" "}
                <span style={{ color: "#38BDF8" }}>Sumur Artesis</span>, karena menembus lapisan akuifer
                bertekanan alami.
              </>
            )}
            {phase === 4 && (
              <>
                Di era modern, teknologi <strong>roller cone drill bit</strong> memungkinkan bor
                menggerus batuan cadas granit keras, mengambil{" "}
                <span style={{ color: "#38BDF8" }}>air tanah murni</span> yang tersaring alami selama
                ribuan tahun!
              </>
            )}
          </p>
        </div>
      </div>
    </AbsoluteFill>
  );
};
